import { AccountingDirectoryControlsComponent } from '../shared/accounting-directory-controls.component';
import { isEditableFinancialDraft } from '../shared/accounting-draft-eligibility';
import { Component, OnInit } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { MenuItem, MessageService } from 'primeng/api';
import { Router } from "@angular/router";
import { SessionStorageService } from "../../services/session-storage.service";
import { FinancePdfViewButtonComponent } from '../finance-pdf-view-button.component';
import { AccountsService } from "../accounts.service";
import { CreditNote } from "./credit-note.model";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { TagModule } from "primeng/tag";
import { DrawerModule } from "primeng/drawer";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../company-scope-selector/company-scope-selector.component';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-credit-note',
    templateUrl: './credit-note.component.html',
    styleUrls: ['../finance-list.shared.css', './credit-note.component.css'],
    standalone: true,
    imports: [FinancePdfViewButtonComponent, TranslocoPipe,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        TableModule,
        ButtonModule,
        TooltipModule,
        ToastModule,
        ProgressBarModule,
        TagModule,
        DrawerModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent,
        AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
    ],
    providers: [
        AccountsService,
        MessageService,
        SessionStorageService
    ]
})
export class CreditNoteComponent implements OnInit {
    readonly pendingStatuses = ['DRAFT', 'SUBMITTED', 'APPROVED', 'ON_HOLD'];
    selectedDocumentStatuses: string[] = this.pendingStatuses.slice();
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    readonly documentActions = new PendingActionTracker<number | undefined>();
    breadcrumbItems: MenuItem[] | undefined;
    homeBreadcrumb: MenuItem | undefined;

    creditNotes: CreditNote[] = [];
    isLoading: boolean = false;
    first = 0;
    rows = 20;
    totalRecords = 0;

    canAddCreditNote = false;
    canEditCreditNote = false;
    previewCreditNote: CreditNote | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(
        private creditNoteService: AccountsService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit(): void {
        this.canAddCreditNote = this.permissionService.hasPermission(app_permissions.accounts.credit_note.create);
        this.canEditCreditNote = this.permissionService.hasPermission(app_permissions.accounts.credit_note.edit);

        this.breadcrumbItems = [
            { label: 'Credit Note' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.loadData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.applyFilters();
    }

    loadData(event?: any) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event?.first ?? 0;
        this.rows = event?.rows ?? 20;
        let page = event?.first ?? 0;
        let pageSize = event?.rows || 20;
        page = page / pageSize;
        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) {
            this.isLoading = false;
            return;
        }
        this.directoryFilters = { owningCompanyId: companyId, companyGroupId, searchText: this.searchText };

        this.creditNoteService.getAllCreditNotes(companyId, page, pageSize, companyGroupId, undefined, undefined, this.selectedDocumentStatuses, this.searchText).subscribe({
            next: (data) => {
                if (requestId !== this.listRequestId) return;
                this.creditNotes = data.items;
                this.totalRecords = data.count;
                this.isLoading = false;
            },
            error: (error) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error loading credit notes'
                });
                this.isLoading = false;
            }
        });
    }

    addCreditNote() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: 'Read-only scope', detail: 'Switch to a legal company to add credit notes.' });
            return;
        }
        this.router.navigate(['/accounts/enterprise/payables/credit-note/add']);
    }

    editCreditNote(creditNote: CreditNote) {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: 'Read-only scope', detail: 'Switch to a legal company to edit credit notes.' });
            return;
        }
        if (!this.isEditable(creditNote)) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Locked document',
                detail: 'Posted, cancelled, voided, reversed, or closed credit notes cannot be edited.'
            });
            return;
        }
        this.router.navigate(['/accounts/enterprise/payables/credit-note/edit', creditNote.creditNoteID]);
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedDocumentStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Total records', value: this.totalRecords, hint: 'Backend count' },
            { label: 'Visible before-tax amount', value: this.formatMoney(this.getVisiblePageTotal()), hint: 'Visible page only', tone: 'info' },
            { label: 'Posted', value: this.countByDocumentStatus('POSTED'), hint: 'Visible page only', tone: 'success' },
            { label: 'Open credits', value: this.creditNotes.filter(note => (note.openAmount || 0) > 0).length, hint: 'Visible page only', tone: 'warn' }
        ];
    }

    get resultText(): string {
        return `${this.totalRecords} credit notes`;
    }

    get activeFilterCount(): number {
        return 0;
    }

    getVisiblePageTotal(): number {
        return this.creditNotes.reduce((total, creditNote) => total + this.getCreditNoteTotalAmountValue(creditNote), 0);
    }

    countByDocumentStatus(status: string): number {
        return this.creditNotes.filter(note => this.getDocumentStatus(note).toUpperCase() === status).length;
    }

    showDate(date: Date | undefined): string {
        if (date === null || date === undefined) return '';
        return date.toLocaleDateString();
    }

    getCreditNoteTotalAmount(creditNote: CreditNote) {
        return this.formatMoney(this.getCreditNoteTotalAmountValue(creditNote), 'SAR');
    }

    getCreditNoteTotalAmountValue(creditNote: CreditNote): number {
        if (creditNote.creditNoteAmount !== undefined && creditNote.creditNoteAmount !== null) {
            return creditNote.creditNoteAmount;
        }
        return creditNote.creditNoteLines?.reduce((total, line) => total + (line.amount || 0), 0) || 0;
    }

    getBeforeTaxAmount(creditNote: CreditNote): number | null {
        if (creditNote.creditNoteAmount != null) return creditNote.creditNoteAmount;
        const lines = creditNote.creditNoteLines;
        return lines?.length && lines.every(line => line.amount != null)
            ? lines.reduce((total, line) => total + (line.amount ?? 0), 0) : null;
    }

    getGrossAmount(creditNote: CreditNote): number | null {
        const beforeTax = this.getBeforeTaxAmount(creditNote);
        return beforeTax != null && creditNote.taxAmount != null ? beforeTax + creditNote.taxAmount : null;
    }

    formatKnownMoney(amount: number | null): string {
        return amount == null ? '—' : this.formatMoney(amount, 'SAR');
    }

    getDocumentStatus(creditNote: CreditNote): string {
        return creditNote.documentStatus || 'POSTED';
    }

    getPostingStatus(creditNote: CreditNote): string {
        return creditNote.postingStatus || 'POSTED';
    }

    getStatusSeverity(status: string | undefined): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch ((status || '').toUpperCase()) {
            case 'POSTED':
            case 'APPROVED':
            case 'PAID':
                return 'success';
            case 'SUBMITTED':
            case 'DRAFT':
                return 'info';
            case 'ON_HOLD':
            case 'CANCELLED':
                return 'warning';
            case 'VOIDED':
            case 'REVERSED':
            case 'FAILED':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    isEditable(creditNote: CreditNote): boolean {
        return isEditableFinancialDraft(creditNote, this.sessionStorageService.getCompany()?.id);
    }

    formatMoney(amount: number | undefined, currencyCode: string = 'SAR'): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    openPreview(creditNote: CreditNote) {
        this.previewCreditNote = creditNote;
        this.isPreviewVisible = true;
        if (!creditNote.creditNoteID) {
            return;
        }
        this.creditNoteService.getCreditNoteById(creditNote.creditNoteID).subscribe({
            next: note => this.previewCreditNote = note,
            error: () => this.previewCreditNote = creditNote
        });
    }

    viewCreditNote(creditNote: CreditNote) {
        this.router.navigate(['/accounts/enterprise/payables/credit-note/view', creditNote.creditNoteID]);
    }

    downloadCreditNote(creditNote: CreditNote) {
        if (!creditNote.creditNoteID || !this.documentActions.begin(creditNote.creditNoteID)) return;
        this.creditNoteService.downloadCreditNoteDocument(creditNote.creditNoteID).pipe(
            finalize(() => this.documentActions.end(creditNote.creditNoteID))
        ).subscribe({
            next: pdf => this.creditNoteService.downloadPdfDocument(pdf, 'credit-note', creditNote.creditNoteNumber, creditNote.creditNoteID, creditNote.postingStatus),
            error: err => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: err?.status === 404 ? 'Original unavailable: no stored credit note PDF exists.' : 'The credit note PDF is unavailable.' })
        });
    }
}
